/**
 * Pillar Color Management Utilities
 * Handles consistent pillar colors across all charts
 */

// Default fallback palette for pillars (matches backend and existing components)
export const DEFAULT_PILLAR_PALETTE = [
  "#BF6E50", "#8CB369", "#D96E48", "#D98E48", "#B58CB3", "#ECBF68",
  "#D9A679", "#56A3A6", "#BFB3A8", "#47A363", "#A6923A", "#5E5E5E",
  "#FF6B35", "#2E86AB", "#A23B72", "#F18F01", "#C73E1D", "#6A994E",
  "#7209B7", "#F72585", "#264653", "#2A9D8F", "#E9C46A", "#F4A261"
];

// Cache for pillar colors to prevent redundant API calls
let pillarColorsCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5000; // 5 seconds

/**
 * Get pillar color configuration from API
 */
export const fetchPillarColors = async (axiosClient) => {
  // Check if we have recent cached data
  const now = Date.now();
  if (pillarColorsCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return pillarColorsCache;
  }

  try {
    const response = await axiosClient.get('/api/pillar-colors');
    // Update cache
    pillarColorsCache = response.data;
    cacheTimestamp = now;
    return response.data;
  } catch (error) {
    console.error('Error fetching pillar colors:', error);
    // Return default structure if API fails
    const defaultData = {
      pillar_color_mappings: {},
      fallback_palette: DEFAULT_PILLAR_PALETTE,
      next_color_index: 0
    };
    // Cache the default data too
    pillarColorsCache = defaultData;
    cacheTimestamp = now;
    return defaultData;
  }
};

/**
 * Clear the pillar colors cache (call this when colors are updated)
 */
export const clearPillarColorsCache = () => {
  pillarColorsCache = null;
  cacheTimestamp = 0;
};

/**
 * Update pillar color configuration via API
 */
export const updatePillarColors = async (axiosClient, pillarColors) => {
  try {
    const response = await axiosClient.put('/api/pillar-colors', pillarColors);
    return response.data;
  } catch (error) {
    console.error('Error updating pillar colors:', error);
    throw error;
  }
};

/**
 * Assign a color to a pillar (auto or manual)
 */
export const assignPillarColor = async (axiosClient, pillarName, color = null) => {
  try {
    const response = await axiosClient.post('/api/pillar-colors/assign', {
      pillar_name: pillarName,
      color: color
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning pillar color:', error);
    throw error;
  }
};

/**
 * Get color for a specific pillar
 * Auto-assigns if not already assigned
 */
export const getPillarColor = async (axiosClient, pillarName, pillarColors = null) => {
  // If pillarColors provided, use it directly
  if (pillarColors && pillarColors.pillar_color_mappings[pillarName]) {
    return pillarColors.pillar_color_mappings[pillarName];
  }
  
  // Otherwise, fetch from API and auto-assign if needed
  try {
    const result = await assignPillarColor(axiosClient, pillarName);
    return result.color;
  } catch (error) {
    console.error(`Error getting color for pillar ${pillarName}:`, error);
    // Fallback to a default color
    const index = pillarName.length % DEFAULT_PILLAR_PALETTE.length;
    return DEFAULT_PILLAR_PALETTE[index];
  }
};

/**
 * Get colors for multiple pillars
 * Returns an object mapping pillar names to colors
 * Ensures consistent color assignment across the application.
 */
export const getPillarColors = async (axiosClient, requestedPillarNames, allOrgPillars = [], pillarColorConfig = null) => {
  const colors = {};
  let config = pillarColorConfig;

  if (!config) {
    config = await fetchPillarColors(axiosClient);
  }

  const { pillar_color_mappings, fallback_palette } = config;

  // Ensure allOrgPillars is populated if not provided
  const uniqueSortedPillars = [...new Set(allOrgPillars.length > 0 ? allOrgPillars : requestedPillarNames)].sort();

  const finalColorMappings = { ...pillar_color_mappings };

  // Assign stable default colors based on alphabetical position
  // Each pillar gets the same fallback color index regardless of custom assignments
  uniqueSortedPillars.forEach((pillarName, index) => {
    if (!finalColorMappings[pillarName]) {
      // Pillar not in existing mappings, assign from fallback palette using stable index
      finalColorMappings[pillarName] = fallback_palette[index % fallback_palette.length];
    }
  });

  // Extract colors for the specifically requested pillar names
  for (const pillarName of requestedPillarNames) {
    if (finalColorMappings[pillarName]) {
      colors[pillarName] = finalColorMappings[pillarName];
    } else {
      // If a requested pillar was somehow not in uniqueSortedPillars (should not happen if allOrgPillars is comprehensive)
      // assign a fallback color to prevent errors using the pillar name hash as fallback
      console.warn(`Pillar ${pillarName} was not found in the initial sorted list for color assignment. Assigning a hash-based fallback.`);
      const hashIndex = pillarName.length % fallback_palette.length;
      colors[pillarName] = fallback_palette[hashIndex];
    }
  }
  
  // Ensure we always return a proper object with string values
  return colors;
};

/**
 * Transform colors for dark mode
 * Adjusts brightness and saturation for better dark mode visibility
 */
export const transformPillarColorForDarkMode = (color) => {
  // Convert hex to HSL
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  // Adjust for dark mode: increase lightness and slightly reduce saturation
  l = Math.min(0.8, l + 0.2); // Increase lightness
  s = Math.max(0.3, s * 0.8); // Slightly reduce saturation
  
  // Convert back to hex
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let r2, g2, b2;
  if (s === 0) {
    r2 = g2 = b2 = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1/3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1/3);
  }
  
  const toHex = (c) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return '#' + toHex(r2) + toHex(g2) + toHex(b2);
};

/**
 * Get theme-aware pillar color
 * Returns the original color or dark mode adjusted color
 */
export const getThemeAwarePillarColor = (color, isDarkMode = false) => {
  return isDarkMode ? transformPillarColorForDarkMode(color) : color;
}; 